import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PricingSection } from '../components/PricingSection';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Users, Clock, Mail, Phone, User, MessageSquare, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
export function PlanTripPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    destination: '',
    duration: '',
    groupSize: '',
    travelDate: '',
    budget: '',
    interests: [] as string[],
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const destinations = ['Nepal', 'Tibet', 'Bhutan', 'Multi-Country'];
  const durations = ['1-5 Days', '6-10 Days', '11-15 Days', '16+ Days'];
  const groupSizes = ['Solo', '2-4 People', '5-10 People', '10+ People'];
  const budgetRanges = ['Under $1,000', '$1,000 - $2,000', '$2,000 - $5,000', '$5,000+'];
  const interestOptions = ['Trekking', 'Cultural Tours', 'Spiritual Journeys', 'Peak Climbing', 'Wildlife Safari', 'Photography'];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
  };
  const handleInterestToggle = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest) ? prev.interests.filter(i => i !== interest) : [...prev.interests, interest]
    }));
  };
  if (isSubmitted) {
    return <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-1818">
        <Navbar data-id="element-1819" />
        <div className="min-h-screen flex items-center justify-center px-4 pt-20" data-id="element-1820">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="max-w-md w-full text-center" data-id="element-1821">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1822">
              <CheckCircle size={40} className="text-green-600" data-id="element-1823" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1824">
              Thank You!
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-1825">
              We've received your trip planning request. Our team will review
              your preferences and get back to you within 24 hours with a
              customized itinerary.
            </p>
            <Button variant="primary" onClick={() => window.location.href = '#'} data-id="element-1826">
              Return to Home
            </Button>
          </motion.div>
        </div>
        <Footer data-id="element-1827" />
      </div>;
  }
  return <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-1828">
      <Navbar data-id="element-1829" />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-gray-900 to-gray-800 py-20 mt-16" data-id="element-1830">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1831">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1832">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-1833">
              Plan Your Dream Trip
            </h1>
            <p className="text-gray-300 text-lg" data-id="element-1834">
              Tell us about your ideal adventure and we'll create a personalized
              itinerary just for you
            </p>
          </motion.div>
        </div>
      </div>

      {/* Pricing Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white" data-id="element-1835">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1836">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} data-id="element-1837">
            <div className="text-center mb-12" data-id="element-1838">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1839">
                Sample Package Pricing
              </h2>
              <p className="text-gray-600 text-lg" data-id="element-1840">
                Get an idea of what's included in our comprehensive trek
                packages
              </p>
            </div>
            <PricingSection data-id="element-1841" />
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-1842">
        <div className="text-center mb-12" data-id="element-1843">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1844">
            Customize Your Trip
          </h2>
          <p className="text-gray-600 text-lg" data-id="element-1845">
            Fill out the form below and we'll create a personalized package for
            you
          </p>
        </div>

        <motion.form initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} onSubmit={handleSubmit} className="space-y-8" data-id="element-1846">
          {/* Personal Information */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8" data-id="element-1847">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-1848">
              <User size={24} className="text-[#0066FF]" data-id="element-1849" />
              Personal Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1850">
              <div data-id="element-1851">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1852">
                  Full Name *
                </label>
                <input type="text" required value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent" placeholder="John Doe" data-id="element-1853" />
              </div>
              <div data-id="element-1854">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1855">
                  Email Address *
                </label>
                <div className="relative" data-id="element-1856">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1857" />
                  <input type="email" required value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent" placeholder="john@example.com" data-id="element-1858" />
                </div>
              </div>
              <div className="md:col-span-2" data-id="element-1859">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1860">
                  Phone Number *
                </label>
                <div className="relative" data-id="element-1861">
                  <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1862" />
                  <input type="tel" required value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent" placeholder="+1 (555) 123-4567" data-id="element-1863" />
                </div>
              </div>
            </div>
          </div>

          {/* Trip Details */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8" data-id="element-1864">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-1865">
              <MapPin size={24} className="text-[#0066FF]" data-id="element-1866" />
              Trip Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1867">
              <div data-id="element-1868">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1869">
                  Destination *
                </label>
                <select required value={formData.destination} onChange={e => setFormData({
                ...formData,
                destination: e.target.value
              })} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent" data-id="element-1870">
                  <option value="" data-id="element-1871">Select destination</option>
                  {destinations.map(dest => <option key={dest} value={dest} data-id="element-1872">
                      {dest}
                    </option>)}
                </select>
              </div>
              <div data-id="element-1873">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1874">
                  Duration *
                </label>
                <div className="relative" data-id="element-1875">
                  <Clock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1876" />
                  <select required value={formData.duration} onChange={e => setFormData({
                  ...formData,
                  duration: e.target.value
                })} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent" data-id="element-1877">
                    <option value="" data-id="element-1878">Select duration</option>
                    {durations.map(dur => <option key={dur} value={dur} data-id="element-1879">
                        {dur}
                      </option>)}
                  </select>
                </div>
              </div>
              <div data-id="element-1880">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1881">
                  Group Size *
                </label>
                <div className="relative" data-id="element-1882">
                  <Users size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1883" />
                  <select required value={formData.groupSize} onChange={e => setFormData({
                  ...formData,
                  groupSize: e.target.value
                })} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent" data-id="element-1884">
                    <option value="" data-id="element-1885">Select group size</option>
                    {groupSizes.map(size => <option key={size} value={size} data-id="element-1886">
                        {size}
                      </option>)}
                  </select>
                </div>
              </div>
              <div data-id="element-1887">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1888">
                  Preferred Travel Date *
                </label>
                <div className="relative" data-id="element-1889">
                  <Calendar size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1890" />
                  <input type="date" required value={formData.travelDate} onChange={e => setFormData({
                  ...formData,
                  travelDate: e.target.value
                })} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent" data-id="element-1891" />
                </div>
              </div>
              <div className="md:col-span-2" data-id="element-1892">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1893">
                  Budget Range *
                </label>
                <select required value={formData.budget} onChange={e => setFormData({
                ...formData,
                budget: e.target.value
              })} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent" data-id="element-1894">
                  <option value="" data-id="element-1895">Select budget range</option>
                  {budgetRanges.map(budget => <option key={budget} value={budget} data-id="element-1896">
                      {budget}
                    </option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Interests */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8" data-id="element-1897">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1898">
              Your Interests
            </h2>
            <p className="text-gray-600 mb-4" data-id="element-1899">Select all that apply</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-1900">
              {interestOptions.map(interest => <button key={interest} type="button" onClick={() => handleInterestToggle(interest)} className={`px-4 py-3 rounded-xl border-2 transition-all font-medium ${formData.interests.includes(interest) ? 'border-[#0066FF] bg-blue-50 text-[#0066FF]' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'}`} data-id="element-1901">
                  {interest}
                </button>)}
            </div>
          </div>

          {/* Additional Information */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8" data-id="element-1902">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-1903">
              <MessageSquare size={24} className="text-[#0066FF]" data-id="element-1904" />
              Additional Information
            </h2>
            <div data-id="element-1905">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1906">
                Tell us more about your dream trip
              </label>
              <textarea value={formData.message} onChange={e => setFormData({
              ...formData,
              message: e.target.value
            })} rows={6} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-transparent resize-none" placeholder="Any specific requirements, dietary restrictions, fitness level, or special requests..." data-id="element-1907" />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center" data-id="element-1908">
            <Button type="submit" variant="primary" size="lg" className="px-12" data-id="element-1909">
              Submit Trip Request
            </Button>
          </div>
        </motion.form>
      </main>

      <Footer data-id="element-1910" />
    </div>;
}